import { useState } from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Icon, Switch, Text, Tooltip } from 'src/components/ui'
import { FetchPrescriptiveModel, ModelInputTag } from 'src/services'
import { useUpdatePrescriptiveModelTagsMutation } from 'pages/site/models/api'
import { Chart } from '../../../OtherFactors'

type Props = {
  model: FetchPrescriptiveModel
  inputTags: ModelInputTag[]
  isEditable?: boolean
  onRemoveInputTag: (tagName: string) => Promise<void>
}

export function InputTagsList({
  model,
  inputTags,
  isEditable = true,
  onRemoveInputTag,
}: Props): JSX.Element {
  const [expandedTags, setExpandedTags] = useState<string[]>([])
  const updateInputTagsMutation = useUpdatePrescriptiveModelTagsMutation()

  const handleExpand = (tagName: string): void => {
    if (expandedTags.includes(tagName)) {
      setExpandedTags(expandedTags.filter(tag => tag !== tagName))
    } else {
      setExpandedTags([...expandedTags, tagName])
    }
  }

  const handleControllableChange = async (
    tagId: string,
    value: boolean,
  ): Promise<void> => {
    const updateData = inputTags.map(tag => {
      if (tag.node.id === tagId) {
        return {
          tagId,
          isControllable: value,
        }
      } else {
        return {
          tagId: tag.node.id,
          isControllable: tag.isControllable,
        }
      }
    })

    await updateInputTagsMutation.mutateAsync({
      inputTags: updateData,
      modelId: model.id,
    })
  }

  return (
    <div className="flex flex-col py-xs">
      <div className="flex justify-between border-0 border-b border-solid border-border px-s py-xs">
        <Text bold>Tagname</Text>
        <Text bold className="pr-m">
          Controllable
        </Text>
      </div>
      {inputTags.map((tag, index) => {
        return (
          <div
            className={classNames(
              index !== inputTags.length - 1 &&
                'border-0 border-b border-solid border-border',
            )}
            key={tag.node.tagName}
          >
            <div
              onClick={() => handleExpand(tag.node.tagName)}
              className="flex cursor-pointer items-center justify-between px-s py-xs transition duration-300 hover:bg-background-hover"
            >
              <Text className="text-right text-text-secondary">
                {tag.node.displayName ?? tag.node.tagName}
              </Text>
              <div
                className="flex items-center gap-s"
                onClick={e => e.stopPropagation()}
              >
                <Switch
                  disabled={!isEditable || updateInputTagsMutation.isLoading}
                  value={tag.isControllable}
                  onChange={value =>
                    handleControllableChange(tag.node.id, value)
                  }
                />
                <Tooltip render={() => <Text>Remove input tag</Text>}>
                  <Icon
                    icon={light('xmark')}
                    className="cursor-pointer"
                    onClick={() => onRemoveInputTag(tag.node.tagName)}
                  />
                </Tooltip>
              </div>
            </div>

            <AnimatePresence>
              {expandedTags.includes(tag.node.tagName) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.1 }}
                  className="py-m"
                >
                  <Chart tag={tag.node} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
